import React from "react";
import DlReportScreen from "./DlReportScreen";
import { formatAmount, formatToISTDate } from "../../../utils/utils";

/**
 * The Daily Loan Collection report - what was collected, and when.
 *
 * TWO DIFFERENT "PAID" FIGURES, DELIBERATELY BOTH SHOWN
 *
 *   Collected In Period - money taken inside the date window
 *   Total Collected     - the loan's whole history since disbursement
 *
 * They are separate columns because they answer different questions, and a
 * report that showed only one of them under the word "paid" would be read as
 * the other. The window figure is the one this report exists for.
 *
 * The date filter here is not cosmetic: it is sent to the server, where it
 * selects which collections are summed. Every other figure - outstanding, PAR,
 * DPD - comes from the same builder the Master report uses, as at the end of the
 * window, so this report reconciles with Master for the same date.
 */
const columns = [
  { label: "Loan Acc No", render: (row) => row?.loanId || "-" },
  { label: "Customer Name", render: (row) => row?.memberName || "-" },
  { label: "Phone", render: (row) => row?.phoneNumber || "-" },
  { label: "Branch", render: (row) => row?.branchName || "-" },
  { label: "Employee", render: (row) => row?.employeeName || row?.username || "-" },
  { label: "Collection Time", render: (row) => row?.collectionTime || "-" },

  {
    label: "Day Of Term",
    render: (row) =>
      row?.dayNumber != null ? `${row.dayNumber} of ${row.instalments}` : "-",
  },
  { label: "Daily Instalment", render: (row) => formatAmount(row?.dailyInstalment || 0) },

  // The window figures - what this report is for.
  { label: "Collected In Period", render: (row) => formatAmount(row?.collected || 0) },
  { label: "Collections In Period", render: (row) => row?.collections ?? 0 },
  {
    label: "First In Period",
    render: (row) =>
      row?.firstCollection ? formatToISTDate(row.firstCollection) : "-",
  },
  {
    label: "Last In Period",
    render: (row) =>
      row?.lastCollection ? formatToISTDate(row.lastCollection) : "-",
  },

  // The whole loan, for context.
  { label: "Total Collected", render: (row) => formatAmount(row?.totalEmiPaid || 0) },
  { label: "Days Collected", render: (row) => row?.emiDaysPaid ?? 0 },
  {
    label: "Outstanding Principal",
    render: (row) => formatAmount(row?.totalOutstandingPrincipal || 0),
  },
  { label: "PAR", render: (row) => formatAmount(row?.totalPAR || 0) },
  { label: "DPD", render: (row) => row?.dpd ?? 0 },
  { label: "Bucket", render: (row) => row?.bucket || "-" },
  { label: "Status", render: (row) => row?.loanStatus || "-" },
];

const renderSummary = (rows) => {
  const collected = rows.reduce((total, row) => total + (row?.collected || 0), 0);
  const collections = rows.reduce(
    (total, row) => total + (row?.collections || 0),
    0
  );
  const collectedFrom = rows.filter((row) => (row?.collections || 0) > 0).length;

  const tiles = [
    { label: "Collected In Period", value: formatAmount(collected) },
    { label: "Collections", value: collections },
    { label: "Customers Paid", value: collectedFrom },
    // The gap that matters to an officer: who did not pay at all.
    { label: "Not Collected", value: rows.length - collectedFrom },
    {
      label: "PAR",
      value: formatAmount(
        rows.reduce((total, row) => total + (row?.totalPAR || 0), 0)
      ),
    },
  ];

  return (
    <div className="d-flex flex-wrap gap-3 px-3 pb-2">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="border rounded p-2 px-3"
          style={{ minWidth: 150 }}
        >
          <div style={{ fontSize: 11, color: "#777" }}>{tile.label}</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{tile.value}</div>
        </div>
      ))}
    </div>
  );
};

const DlCollectionReport = () => (
  <DlReportScreen
    title="DL Collection Report"
    endpoint="getDlCollectionReport"
    reportName="DlCollectionReport"
    columns={columns}
    renderSummary={renderSummary}
  />
);

export default DlCollectionReport;
